import React, {
  ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";
import { UserType } from "../../../graphql/types";
import { UserTypeContext } from "../../../contexts/UserTypeContext";
import { RoutePaths } from "../../../enumStrings";
import FullPageLoader from "../../common/FullPageLoader";
import { STAFF_SUPPORT_EMAIL } from "../../../utils/userFacingError";
import { useNavigate } from "react-router-dom";
import { useGetLoggedInAppInfo } from "../../../hooks/useGetLoggedInAppInfo";

// User can only access this if logged in so no need to check if logged in here
const Root = (): ReactElement => {
  const navigate = useNavigate();
  const [hasNoAllowedDestination, setHasNoAllowedDestination] =
    useState(false);

  const {
    state: { userType },
  } = useContext(UserTypeContext);

  const {
    firstAllowedExternalPathForUser,
    firstAllowedPathThatShouldBeConsideredTheHomePageForUser,
    userInfoLoading,
    userInfoError,
  } = useGetLoggedInAppInfo();

  useEffect(() => {
    if (userType && !userInfoLoading && !userInfoError) {
      if (
        userType !== UserType.ChamberAdmin &&
        userType !== UserType.ChamberStaff
      ) {
        navigate(RoutePaths.login);
      } else if (firstAllowedPathThatShouldBeConsideredTheHomePageForUser) {
        navigate(firstAllowedPathThatShouldBeConsideredTheHomePageForUser);
      } else if (firstAllowedExternalPathForUser) {
        window.location.assign(firstAllowedExternalPathForUser);
      } else {
        setHasNoAllowedDestination(true);
      }
    }
  }, [
    userType,
    navigate,
    firstAllowedPathThatShouldBeConsideredTheHomePageForUser,
    firstAllowedExternalPathForUser,
    userInfoLoading,
    userInfoError,
  ]);

  if (userInfoError) {
    return (
      <div className="mx-auto mt-20 max-w-xl rounded-lg border border-red-200 bg-red-50 p-6 text-center text-red-900">
        <p className="font-semibold">
          We could not load your dashboard. Check your internet connection,
          then select Try again.
        </p>
        <p className="mt-2">
          {`If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`}
        </p>
        <button
          type="button"
          className="mt-4 rounded-md border border-red-300 bg-white px-4 py-2 font-semibold"
          onClick={() => window.location.reload()}
        >
          Try again
        </button>
      </div>
    );
  }

  if (hasNoAllowedDestination) {
    return (
      <div className="mx-auto mt-20 max-w-xl rounded-lg border border-amber-200 bg-amber-50 p-6 text-center text-amber-900">
        Your staff account cannot open any dashboard pages yet. Ask your chamber
        administrator to give you access in Chamber Set Up &gt; Create Employees
        and Permissions.
      </div>
    );
  }

  return <FullPageLoader />;
};

export default Root;
